import { TASK_STATUSES, type TaskStatus } from "./tasks-api";

/**
 * Activity metrics — a small, scope-generalizable set of "what's happening in
 * the app" numbers, surfaced both on the workspace dashboard (one workspace)
 * and on the super-admin dashboard (platform-wide, across all workspaces).
 *
 * The wire shapes here are the single source of truth shared by the API
 * serializer, the two route handlers, and the frontend. Everything is keyed
 * by the canonical {@link TASK_STATUSES}, so adding a status propagates here
 * automatically.
 */

/** Which slice of the platform an overview was computed over. */
export type ActivityMetricsScopeKind = "workspace" | "platform";

/**
 * Non-terminal task statuses — the ones that count as "open" / still needing
 * work. Derived from the canonical list so it stays exhaustive.
 */
export const OPEN_TASK_STATUSES = TASK_STATUSES.filter(
  (status) => status !== "done" && status !== "canceled",
) as readonly TaskStatus[];

/** Per-status task counts for a single day, keyed by canonical status. */
export type TaskStatusCounts = Record<TaskStatus, number>;

/** One day in the task-status-by-day series. */
export interface TaskStatusDayBucket {
  /** Calendar day in `YYYY-MM-DD` (UTC). */
  date: string;
  /** Count of tasks created that day, by current status. Missing = 0. */
  counts: TaskStatusCounts;
}

/**
 * Age buckets for the open-task aging report, oldest last. The `bucket` keys
 * are stable wire values; `label` is the display string. Boundaries are in
 * days (`maxDays` is exclusive; the final bucket is open-ended).
 */
export const TASK_AGING_BUCKETS = [
  { bucket: "lt_1d", label: "< 1 day", maxDays: 1 },
  { bucket: "d1_3", label: "1–3 days", maxDays: 3 },
  { bucket: "d3_7", label: "3–7 days", maxDays: 7 },
  { bucket: "w1_2", label: "1–2 weeks", maxDays: 14 },
  { bucket: "w2_4", label: "2–4 weeks", maxDays: 30 },
  { bucket: "gt_30d", label: "> 30 days", maxDays: null },
] as const;

export type TaskAgingBucketKey = (typeof TASK_AGING_BUCKETS)[number]["bucket"];

/** One age bucket of currently-open tasks, broken down by status. */
export interface TaskAgingBucket {
  /** Stable bucket key (see {@link TASK_AGING_BUCKETS}). */
  bucket: TaskAgingBucketKey;
  /** Display label for the bucket. */
  label: string;
  /** Count of open tasks in this age range, by status. Missing = 0. */
  counts: TaskStatusCounts;
}

/** Live "right now" activity counts (snake_case wire shape). */
export interface ActivityCounts {
  /** Tasks in a non-terminal status (see {@link OPEN_TASK_STATUSES}). */
  open_tasks: number;
  /** Workflow runs currently queued or running. */
  active_workflow_runs: number;
  /** Distinct agents with an in-progress run attached to a task. */
  active_agents: number;
}

/** Full activity overview payload returned by both metrics endpoints. */
export interface ActivityMetricsOverview {
  scope: ActivityMetricsScopeKind;
  /** Number of days covered by `task_status_by_day`. */
  days: number;
  /** ISO timestamp the overview was generated. */
  generated_at: string;
  counts: ActivityCounts;
  /** One bucket per day, oldest first, with no gaps. */
  task_status_by_day: TaskStatusDayBucket[];
  /** Open tasks bucketed by age, one entry per {@link TASK_AGING_BUCKETS}. */
  task_aging: TaskAgingBucket[];
}

/** Bounds for the chart window (the "fidelity" knob on both endpoints). */
export const ACTIVITY_METRICS_MIN_DAYS = 1;
export const ACTIVITY_METRICS_MAX_DAYS = 90;
export const ACTIVITY_METRICS_DEFAULT_DAYS = 7;
